import type { CommunityAiSetting } from "@circle-react/types/CommunityAiSetting";
import type { CurrentCommunity } from "@circle-react/types/CurrentCommunity";
import type { DeepPartial } from "react-hook-form";

import {
  fetchRequest,
  reactQueryDelete,
  reactQueryGet,
  reactQueryPatch,
  reactQueryPost,
  reactQueryPut,
} from "../helpers/backendRequestHelpers";
import { internalApi } from "../helpers/urlHelpers";

const show = <T = unknown>() =>
  fetchRequest<T>(internalApi.communities.show(), {
    method: "GET",
  });

export interface UpdatePayload {
  community?: DeepPartial<CurrentCommunity>;
  community_ai_setting?: Partial<CommunityAiSetting>;
  community_setting?: DeepPartial<CurrentCommunity>;
  disable_access_groups?: boolean;
}

const update = (payload: UpdatePayload) =>
  reactQueryPut<CurrentCommunity, UpdatePayload>(
    internalApi.communities.update(),
    payload,
  );

const communitySettings = <T = unknown>() =>
  fetchRequest<T>(internalApi.communities.communitySettings.index(), {
    method: "GET",
  });

const updateOnboardingSettings = (payload: any) =>
  reactQueryPatch(
    internalApi.communities.onboarding_settings.update(),
    payload,
  );

const getCustomInvitationEmail = () =>
  reactQueryGet(internalApi.communities.customInvitationEmail.show());

const updateCustomInvitationEmail = (payload: any) =>
  reactQueryPatch(
    internalApi.communities.customInvitationEmail.update(),
    payload,
  );

const getInvitationScanStatus = () =>
  reactQueryGet(internalApi.communities.customInvitationEmail.scanStatus());

const getTemplates = (communityId: string) =>
  reactQueryGet(
    internalApi.communities.templates.index({
      communityId,
    }),
  );

const assignTemplate = (communityId: string, payload: any) =>
  reactQueryPost(
    internalApi.communities.templates.create({
      communityId,
    }),
    payload,
  );

const submitQuestions = (communityId: string, payload: any) =>
  reactQueryPost(
    internalApi.communities.questions.create({
      communityId,
    }),
    payload,
  );

const createCommunity = (payload: any) =>
  reactQueryPost(internalApi.communities.create(), payload);

const getCustomDomain = () => reactQueryGet(internalApi.customDomain.show());

const updateCustomDomain = (payload: any) =>
  reactQueryPost(internalApi.customDomain.update(), payload);

const removeCustomDomain = () =>
  reactQueryDelete(internalApi.customDomain.destroy());

const updateLandingPageSettings = () =>
  reactQueryPost(internalApi.landingPageSettings.update());

const getOnboardingSettings = () =>
  reactQueryGet(internalApi.communities.onboarding_settings.show());

export const communityApi = {
  show,
  update,
  communitySettings,
  updateOnboardingSettings,
  getCustomInvitationEmail,
  updateCustomInvitationEmail,
  getInvitationScanStatus,
  getTemplates,
  assignTemplate,
  submitQuestions,
  createCommunity,
  getCustomDomain,
  updateCustomDomain,
  removeCustomDomain,
  updateLandingPageSettings,
  getOnboardingSettings,
};
